import { useMemo } from "react";
import { useSelector } from "react-redux";

import { canWriteCommission, canWriteFeature } from "@/lib/authScope";
import type { SeatScope } from "@/lib/seatScope";
import { type RootState } from "@/store/store";

/**
 * The caller's hierarchy seat, for scoping the UI to their own subtree.
 *
 * `usePermissions` answers "what level is this user?" from the JWT alone;
 * this answers "which nodes may they pick?", which needs the full ancestor
 * chain that only `/auth/me` supplies.
 */
export function useSeatScope(): SeatScope {
  const user = useSelector((state: RootState) => state.auth.user);

  return useMemo(() => {
    const isPlatform = user?.source === "pglx";
    // Seated on ANY hierarchy id, not just clientId: the token carries only the
    // caller's own level, so keying off clientId alone reads every employer,
    // group, division and location seat as unseated until the rest of the
    // chain arrives.
    const hasSeat =
      user?.clientId != null ||
      user?.employerId != null ||
      user?.groupId != null ||
      user?.divisionId != null ||
      user?.locationId != null;

    return {
      isPlatform,
      // `source`/`role` are set from the token in the same dispatch as the
      // hierarchy ids, so their presence means the seat is resolved — platform
      // staff legitimately have no ids and must not read as still loading.
      isKnown: user != null && (user.source != null || user.role != null),
      isSeated: !isPlatform && hasSeat,
      clientId: user?.clientId,
      employerId: user?.employerId,
      groupId: user?.groupId,
      divisionId: user?.divisionId,
      locationId: user?.locationId,
      // The backend's own list is authoritative. A session minted before it
      // started sending one falls back to the role mirror, so an existing
      // administrator is not locked out of commission until they re-login.
      canWriteCommission: user?.writableFeatures
        ? canWriteFeature(user.writableFeatures, "COMMISSION")
        : canWriteCommission(user?.role),
    };
  }, [user]);
}
